// components/Dashboard/AudioPlayer.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';

interface AudioPlayerProps {
    url: string;
    duration?: number;
    timestamp?: Date | string;
    compact?: boolean;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
                                                            url,
                                                            duration,
                                                            compact = false
                                                        }) => {
    const { t, isRTL } = useLanguage();
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [audioDuration, setAudioDuration] = useState(duration || 0);
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleLoadedMetadata = () => {
            if (audio.duration && isFinite(audio.duration)) {
                setAudioDuration(audio.duration);
            }
            setIsLoaded(true);
            setError(null);
        };

        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);

        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        const handleError = (e: Event) => {
            console.error('Audio load error:', e, audio.error);
            setError(t('audioPlayer.errors.loadFailed'));
            setIsPlaying(false);
        };

        const handleCanPlay = () => {
            setIsLoaded(true);
            setError(null);
        };

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);
        audio.addEventListener('canplay', handleCanPlay);

        audio.load();

        return () => {
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
            audio.removeEventListener('canplay', handleCanPlay);
        };
    }, [url]);

    const togglePlay = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const audio = audioRef.current;
        if (!audio) {
            return;
        }

        try {
            if (isPlaying) {
                audio.pause();
                setIsPlaying(false);
            } else {
                if (audio.readyState < 2) {
                    await new Promise((resolve, reject) => {
                        const onCanPlay = () => {
                            audio.removeEventListener('canplay', onCanPlay);
                            audio.removeEventListener('error', onError);
                            resolve(null);
                        };
                        const onError = (err: Event) => {
                            audio.removeEventListener('canplay', onCanPlay);
                            audio.removeEventListener('error', onError);
                            reject(err);
                        };
                        audio.addEventListener('canplay', onCanPlay);
                        audio.addEventListener('error', onError);
                    });
                }

                const playPromise = audio.play();
                if (playPromise !== undefined) {
                    await playPromise;
                    setIsPlaying(true);
                    setError(null);
                }
            }
        } catch (err: any) {
            console.error('Audio playback error:', err);
            setError(t('audioPlayer.errors.playFailed'));
            setIsPlaying(false);
        }
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const audio = audioRef.current;
        if (!audio) return;
        const newTime = Number(e.target.value);
        audio.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const formatTime = (seconds: number) => {
        if (!seconds || !isFinite(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const progressPercent = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

    // =====================================
    // COMPACT VERSION (list view)
    // =====================================
    if (compact) {
        return (
            <div
                className="inline-flex items-center gap-2 bg-[#D6FFEA] px-3 py-1.5 rounded-full border border-[#00E784]/30"
                dir={isRTL ? 'rtl' : 'ltr'}
            >
                <button
                    type="button"
                    onClick={togglePlay}
                    className="w-6 h-6 bg-[#00E784] rounded-full flex items-center justify-center hover:bg-[#00EC84] transition-colors flex-shrink-0"
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                    {isPlaying ? (
                        <Pause className="w-3 h-3 text-[#00493E]" />
                    ) : (
                        <Play className="w-3 h-3 text-[#00493E]" style={{ marginLeft: '1px' }} />
                    )}
                </button>
                <span className="text-xs font-semibold text-[#00493E]" dir="ltr">
                    {formatTime(isPlaying ? currentTime : audioDuration)}
                </span>
                <audio
                    ref={audioRef}
                    src={url}
                    preload="metadata"
                />
            </div>
        );
    }

    // =====================================
    // FULL VERSION (detail view)
    // =====================================
    return (
        <div
            className="bg-gradient-to-r from-[#D6FFEA] to-[#FAFFFC] p-4 rounded-2xl border-2 border-[#00E784]/30 shadow-sm"
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            {/* Header */}
            <div className="mb-3">
                <h4 className={`font-bold text-sm text-[#00493E] ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('audioPlayer.title')}
                </h4>
                <p className={`text-xs text-gray-600 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('audioPlayer.subtitle')}
                </p>
            </div>

            {/* Player row */}
            <div className="flex items-center gap-3">
                {/* Play button */}
                <button
                    type="button"
                    onClick={togglePlay}
                    className="w-12 h-12 bg-gradient-to-br from-[#00E784] to-[#00EC84] rounded-full flex items-center justify-center hover:shadow-lg hover:scale-105 transition-all flex-shrink-0"
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                    {isPlaying ? (
                        <Pause className="w-5 h-5 text-[#00493E]" />
                    ) : (
                        <Play className="w-5 h-5 text-[#00493E]" style={{ marginLeft: '2px' }} />
                    )}
                </button>

                {/* Progress bar + time */}
                <div className="flex-1 space-y-1">
                    <input
                        type="range"
                        min={0}
                        max={audioDuration || 0}
                        value={currentTime}
                        onChange={handleSeek}
                        disabled={!isLoaded}
                        className="w-full h-2 bg-white rounded-full appearance-none cursor-pointer"
                        style={{
                            direction: 'ltr',
                            background: `linear-gradient(to right, #00E784 0%, #00E784 ${progressPercent}%, #ffffff ${progressPercent}%, #ffffff 100%)`
                        }}
                    />
                    <div className="flex justify-between text-xs text-[#00493E] font-medium" dir="ltr">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(audioDuration)}</span>
                    </div>
                </div>

                {/* Volume icon */}
                <Volume2 className="w-4 h-4 text-[#00493E]/60 flex-shrink-0" />
            </div>

            {/* Error message */}
            {error && (
                <div className={`mt-2 text-xs text-red-600 ${isRTL ? 'text-right' : 'text-left'}`}>
                    ⚠️ {error}
                </div>
            )}

            <audio
                ref={audioRef}
                src={url}
                preload="metadata"
            />
        </div>
    );
};
